import { useEffect } from 'react';
import { ActivityTracker, activityTaken, type ActivitySender } from '@shared/activity-tracker';
import {
  activityFromPath,
  type ActivityContext,
  type TimeRecordKind,
  type TimeRecordRef,
} from '@shared/time-tracking';
import { postActivity } from '@/lib/activity-api';

/** How often a tab checks whether it's active, and how often it sends what it counted. */
const SAMPLE_MS = 15_000;
const FLUSH_MS = 60_000;
/** Scrolling fires many events: count at most one a second. */
const INPUT_THROTTLE_MS = 1_000;

const INPUT_EVENTS = ['keydown', 'pointerdown', 'wheel', 'scroll', 'touchstart'] as const;

const sendActivity: ActivitySender = async (minutes, { keepalive }) =>
  activityTaken((await postActivity(minutes, { keepalive })).status);

/** Records open in a dialog the address doesn't name (a purchase order being edited), newest last. */
const openRecords: TimeRecordRef[] = [];

/**
 * Where the tab is: the area and record its address names (shared/time-tracking.ts), or the record open
 * in a dialog over it (`useActivityRecord`).
 */
export function currentActivityContext(
  pathname: string,
  search: string,
  dialogRecord: TimeRecordRef | null = openRecords.at(-1) ?? null
): ActivityContext {
  const { area, record } = activityFromPath(pathname, search);
  const shown = dialogRecord ?? record;
  return { area, record_kind: shown?.kind ?? null, record_id: shown?.id ?? null, detail: null };
}

let users = 0;
let stop: (() => void) | null = null;

/** Starts this tab's one tracker (once, however many layouts ask). → how to stop it. */
function start(): () => void {
  const tracker = new ActivityTracker(
    {
      now: () => Date.now(),
      visible: () => document.visibilityState === 'visible',
      focused: () => document.hasFocus(),
      context: () => currentActivityContext(window.location.pathname, window.location.search),
    },
    sendActivity
  );
  let lastInput = 0;
  const onInput = () => {
    const now = Date.now();
    if (now - lastInput < INPUT_THROTTLE_MS) return;
    lastInput = now;
    tracker.input();
  };
  const onHide = () => {
    if (document.visibilityState === 'hidden') void tracker.flush(true);
  };
  const onPageHide = () => void tracker.flush(true);
  const opts: AddEventListenerOptions = { capture: true, passive: true };
  for (const name of INPUT_EVENTS) window.addEventListener(name, onInput, opts);
  document.addEventListener('visibilitychange', onHide);
  window.addEventListener('pagehide', onPageHide);
  const sampler = setInterval(() => tracker.sample(), SAMPLE_MS);
  const flusher = setInterval(() => void tracker.flush(), FLUSH_MS);
  return () => {
    for (const name of INPUT_EVENTS) window.removeEventListener(name, onInput, opts);
    document.removeEventListener('visibilitychange', onHide);
    window.removeEventListener('pagehide', onPageHide);
    clearInterval(sampler);
    clearInterval(flusher);
    void tracker.flush(true);
  };
}

/**
 * Time tracking (T-77): counts this signed-in tab's active minutes and sends them to POST /api/activity
 * about once a minute, and when the tab is hidden or closed. Mounted in the team layout. A browser
 * driven by a script (Playwright, the dev screenshots) never counts.
 */
export function useActivityTracking(): void {
  useEffect(() => {
    if (typeof window === 'undefined' || navigator.webdriver) return undefined;
    users += 1;
    if (users === 1) stop = start();
    return () => {
      users -= 1;
      if (users === 0) {
        stop?.();
        stop = null;
      }
    };
  }, []);
}

/**
 * While a dialog shows a record the address doesn't name (a purchase order being edited), the tab's
 * minutes are that record's. Pass a null id while nothing is open.
 */
export function useActivityRecord(kind: TimeRecordKind, id: string | null | undefined): void {
  useEffect(() => {
    if (!id) return undefined;
    const ref: TimeRecordRef = { kind, id: id.toLowerCase() };
    openRecords.push(ref);
    return () => {
      const at = openRecords.lastIndexOf(ref);
      if (at >= 0) openRecords.splice(at, 1);
    };
  }, [kind, id]);
}
